/**
 * Build a blob URL for `audioWorklet.addModule`.
 *
 * Prefer a bundled worklet from `workletMain.ts` (as Adam serves at
 * `/curva-audio-worklet.js`) — that path includes osc3 / FM / noise / key track.
 *
 * This blob fallback is a reduced kernel kept for callers that do not pass
 * `workletUrl`. It intentionally stays small and may lag the TS kernel.
 *
 * For production lab/editor: pass `createAudioEngine({ workletUrl })` with the
 * bundled processor.
 */
export const CURVA_AUDIO_PROCESSOR_NAME = "curva-audio-processor";

export function createWorkletModuleUrl(): string {
  const source = `"use strict";
const INSTRUMENT_SLOT_COUNT = 32;
const DEFAULT_PRESETS = ${JSON.stringify(minimalDefaultPresets())};

function clonePreset(p) { return JSON.parse(JSON.stringify(p)); }
function midiToHz(note) { return 440 * Math.pow(2, (note - 69) / 12); }
function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

function fxParam(node, key, fallback) {
  const v = node.params && node.params[key];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
function fxDelayBuf(sr, maxMs) { return new Float32Array(Math.max(1, Math.ceil(sr * maxMs / 1000))); }
function fxReadDelay(buf, write, delaySamples) {
  const len = buf.length;
  const read = (write - delaySamples + len * 4) % len;
  const i0 = Math.floor(read); const i1 = (i0 + 1) % len; const frac = read - i0;
  return buf[i0] * (1 - frac) + buf[i1] * frac;
}
function createFxRuntime(node, sr) {
  if (node.type === "bitcrush") return { type: "bitcrush", hold: 0, counter: 0 };
  if (node.type === "delay") return { type: "delay", buffer: fxDelayBuf(sr, 2000), write: 0 };
  if (node.type === "chorus") return { type: "chorus", buffer: fxDelayBuf(sr, 50), write: 0, phase: 0 };
  if (node.type === "reverb") {
    const comb = (ms) => ({ buffer: fxDelayBuf(sr, ms), index: 0, filterStore: 0 });
    const ap = (ms) => ({ buffer: fxDelayBuf(sr, ms), index: 0 });
    return { type: "reverb", combs: [comb(29.7), comb(37.1), comb(41.1), comb(43.7)], allpasses: [ap(5), ap(1.7)] };
  }
  if (node.type === "colorReverb") {
    return {
      type: "colorReverb",
      predelay: new Float32Array(223), pdPos: 0,
      d1: new Float32Array(443), d2: new Float32Array(661),
      d3: new Float32Array(997), d4: new Float32Array(1499),
      p1: 0, p2: 0, p3: 0, p4: 0, lp1: 0, lp2: 0, lp3: 0, lp4: 0,
    };
  }
  if (node.type === "compressor") {
    return { type: "compressor", env: 0, hpfY: 0, hpfX1: 0 };
  }
  if (node.type === "eq") {
    const u = () => ({ b0: 1, b1: 0, b2: 0, a1: 0, a2: 0, x1: 0, x2: 0, y1: 0, y2: 0 });
    return { type: "eq", bands: [u(), u(), u(), u(), u()], lastGains: [NaN, NaN, NaN, NaN, NaN], lastSr: 0 };
  }
  return { type: "distortion" };
}
function fxFinite(v) { return Number.isFinite(v) ? v : 0; }
function processFxOne(input, node, rt, sr) {
  if (rt.type === "distortion") {
    return Math.tanh(fxFinite(input) * Math.max(1, fxParam(node, "drive", 8)));
  }
  if (rt.type === "bitcrush") {
    const bits = Math.min(16, Math.max(1, fxParam(node, "bits", 8)));
    const ds = Math.max(1, Math.floor(fxParam(node, "downsample", 4)));
    rt.counter++;
    if (rt.counter >= ds) {
      rt.counter = 0;
      const levels = Math.pow(2, bits);
      rt.hold = Math.round(fxFinite(input) * (levels / 2)) / (levels / 2);
    }
    return fxFinite(rt.hold);
  }
  if (rt.type === "delay") {
    const timeMs = Math.min(2000, Math.max(1, fxParam(node, "timeMs", 250)));
    const fb = Math.min(0.95, Math.max(0, fxParam(node, "feedback", 0.35)));
    const delayed = fxFinite(fxReadDelay(rt.buffer, rt.write, Math.min(rt.buffer.length - 1, (timeMs / 1000) * sr)));
    const written = fxFinite(input) + delayed * fb;
    rt.buffer[rt.write] = Number.isFinite(written) ? written : 0;
    rt.write = (rt.write + 1) % rt.buffer.length;
    return delayed;
  }
  if (rt.type === "chorus") {
    const rate = Math.max(0.01, fxParam(node, "rateHz", 1.2));
    const depthMs = Math.max(0, fxParam(node, "depthMs", 3.5));
    const baseMs = Math.max(0.5, fxParam(node, "delayMs", 8));
    rt.phase += rate / sr; if (rt.phase >= 1) rt.phase -= 1;
    const delaySamples = Math.min(rt.buffer.length - 2, ((baseMs + Math.sin(2 * Math.PI * rt.phase) * depthMs) / 1000) * sr);
    const delayed = fxFinite(fxReadDelay(rt.buffer, rt.write, delaySamples));
    rt.buffer[rt.write] = fxFinite(input);
    rt.write = (rt.write + 1) % rt.buffer.length;
    return delayed;
  }
  if (rt.type === "reverb") {
    const room = Math.min(0.98, Math.max(0, fxParam(node, "room", 0.55)));
    const damp = Math.min(1, Math.max(0, fxParam(node, "damp", 0.4)));
    let sum = 0;
    for (const comb of rt.combs) {
      const delayed = fxFinite(comb.buffer[comb.index]);
      let store = fxFinite(comb.filterStore);
      store = delayed * (1 - damp) + store * damp;
      const written = fxFinite(input) + store * room;
      if (!Number.isFinite(written) || !Number.isFinite(store)) {
        comb.filterStore = 0; comb.buffer[comb.index] = 0;
      } else {
        comb.filterStore = store; comb.buffer[comb.index] = written;
      }
      comb.index = (comb.index + 1) % comb.buffer.length;
      sum += delayed;
    }
    let out = sum / rt.combs.length;
    for (const ap of rt.allpasses) {
      const bufOut = fxFinite(ap.buffer[ap.index]);
      const o = -fxFinite(out) + bufOut;
      const written = fxFinite(out) + bufOut * 0.5;
      ap.buffer[ap.index] = Number.isFinite(written) ? written : 0;
      ap.index = (ap.index + 1) % ap.buffer.length;
      out = o;
    }
    return fxFinite(out);
  }
  if (rt.type === "colorReverb") {
    const preGain = Math.min(2, Math.max(0, fxParam(node, "preGain", 1)));
    const gain = Math.min(2, Math.max(0, fxParam(node, "gain", 1)));
    const fb = Math.min(0.98, Math.max(0, fxParam(node, "feedback", 0.7)));
    const damping = Math.min(0.99, Math.max(0, fxParam(node, "damping", 0.55)));
    const ducking = Math.min(1, Math.max(0, fxParam(node, "ducking", 0.7)));
    const width = Math.min(1, Math.max(0, fxParam(node, "width", 0.3)));
    const pdMix = Math.min(1, Math.max(0, fxParam(node, "predelayMix", 0.1)));
    const satWetAmt = Math.min(1, Math.max(0, fxParam(node, "saturateWet", 0.1)));
    const satDrive = Math.min(12, Math.max(1, fxParam(node, "satDrive", 3.5)));
    const dryIn = fxFinite(input) * preGain;
    const pdOut = fxFinite(rt.predelay[rt.pdPos]);
    rt.predelay[rt.pdPos] = dryIn;
    rt.pdPos = (rt.pdPos + 1) % rt.predelay.length;
    const excite = dryIn * (1 - pdMix) + pdOut * pdMix;
    const y1 = fxFinite(rt.d1[rt.p1]); const y2 = fxFinite(rt.d2[rt.p2]);
    const y3 = fxFinite(rt.d3[rt.p3]); const y4 = fxFinite(rt.d4[rt.p4]);
    const dampStep = (x, prev) => { const a = Math.min(0.999, Math.max(0, damping)); return x * (1 - a) + prev * a; };
    rt.lp1 = dampStep(y1, rt.lp1); rt.lp2 = dampStep(y2, rt.lp2);
    rt.lp3 = dampStep(y3, rt.lp3); rt.lp4 = dampStep(y4, rt.lp4);
    const h1 = (rt.lp1 + rt.lp2 + rt.lp3 + rt.lp4) * 0.5;
    const h2 = (rt.lp1 - rt.lp2 + rt.lp3 - rt.lp4) * 0.5;
    const h3 = (rt.lp1 + rt.lp2 - rt.lp3 - rt.lp4) * 0.5;
    const h4 = (rt.lp1 - rt.lp2 - rt.lp3 + rt.lp4) * 0.5;
    const soft = (x) => x - x * x * x * 0.18;
    rt.d1[rt.p1] = fxFinite(soft(excite + h2 * fb));
    rt.d2[rt.p2] = fxFinite(soft(excite + h3 * fb));
    rt.d3[rt.p3] = fxFinite(soft(excite + h4 * fb));
    rt.d4[rt.p4] = fxFinite(soft(excite + h1 * fb));
    rt.p1 = (rt.p1 + 1) % rt.d1.length; rt.p2 = (rt.p2 + 1) % rt.d2.length;
    rt.p3 = (rt.p3 + 1) % rt.d3.length; rt.p4 = (rt.p4 + 1) % rt.d4.length;
    let wetL = y1 * 0.62 + y2 * 0.18 + y3 * 0.31 - y4 * 0.12;
    let wetR = y1 * -0.1 + y2 * 0.58 - y3 * 0.14 + y4 * 0.36;
    const mid = 0.5 * (wetL + wetR);
    let side = 0.5 * (wetL - wetR) * width * 1.8;
    wetL = mid + side; wetR = mid - side;
    let wet = 0.5 * (wetL + wetR);
    let duck = 1 - Math.min(1, Math.abs(dryIn)) * ducking;
    duck = Math.min(1, Math.max(0.12, duck));
    wet *= duck;
    const sat = Math.tanh(wet * satDrive) / Math.tanh(satDrive);
    wet = wet * (1 - satWetAmt) + sat * satWetAmt;
    wet *= gain;
    return Math.min(0.95, Math.max(-0.95, fxFinite(wet)));
  }
  if (rt.type === "compressor") {
    const thr = Math.min(0, Math.max(-60, fxParam(node, "thresholdDb", -18)));
    const ratio = Math.min(20, Math.max(1, fxParam(node, "ratio", 4)));
    const atkMs = Math.min(200, Math.max(0.1, fxParam(node, "attackMs", 12)));
    const relMs = Math.min(2000, Math.max(1, fxParam(node, "releaseMs", 120)));
    const knee = Math.min(24, Math.max(0, fxParam(node, "kneeDb", 6)));
    const makeup = Math.min(24, Math.max(-24, fxParam(node, "makeupDb", 2)));
    const hpfHz = Math.min(400, Math.max(0, fxParam(node, "detectorHpfHz", 80)));
    const x = fxFinite(input);
    let det = x;
    if (hpfHz > 0.5) {
      const rc = 1 / (2 * Math.PI * hpfHz);
      const dt = 1 / Math.max(1, sr);
      const a = rc / (rc + dt);
      rt.hpfY = a * (rt.hpfY + x - rt.hpfX1);
      rt.hpfX1 = x;
      det = rt.hpfY;
    }
    const level = Math.abs(det);
    const atk = 1 - Math.exp(-1 / (Math.max(0.0001, atkMs / 1000) * sr));
    const rel = 1 - Math.exp(-1 / (Math.max(0.001, relMs / 1000) * sr));
    if (level > rt.env) rt.env += (level - rt.env) * atk;
    else rt.env += (level - rt.env) * rel;
    if (!Number.isFinite(rt.env) || rt.env < 0) rt.env = 0;
    const levelDb = 20 * Math.log10(rt.env + 1e-8);
    const slope = 1 / ratio - 1;
    const half = knee * 0.5;
    const over = levelDb - thr;
    let gr = 0;
    if (over > -half) {
      if (half > 0 && over < half) {
        const t = over + half;
        gr = slope * (t * t) / (2 * knee);
      } else {
        gr = over * slope;
      }
    }
    return fxFinite(x * Math.pow(10, (gr + makeup) / 20));
  }
  if (rt.type === "eq") {
    const gains = [
      Math.min(18, Math.max(-18, fxParam(node, "lowDb", 0))),
      Math.min(18, Math.max(-18, fxParam(node, "lowMidDb", 0))),
      Math.min(18, Math.max(-18, fxParam(node, "midDb", 0))),
      Math.min(18, Math.max(-18, fxParam(node, "highMidDb", 0))),
      Math.min(18, Math.max(-18, fxParam(node, "highDb", 0))),
    ];
    const specs = [
      { kind: "low", f: 80, q: 0.7 },
      { kind: "peak", f: 250, q: 0.9 },
      { kind: "peak", f: 1000, q: 0.9 },
      { kind: "peak", f: 4000, q: 0.9 },
      { kind: "high", f: 10000, q: 0.7 },
    ];
    if (rt.lastSr !== sr || gains.some((g, i) => g !== rt.lastGains[i])) {
      rt.lastSr = sr;
      rt.lastGains = gains.slice();
      for (let i = 0; i < 5; i++) {
        const b = rt.bands[i]; const g = gains[i]; const sp = specs[i];
        if (Math.abs(g) < 0.01) { b.b0 = 1; b.b1 = 0; b.b2 = 0; b.a1 = 0; b.a2 = 0; continue; }
        const A = Math.pow(10, g / 40);
        const w0 = 2 * Math.PI * Math.min(sr * 0.45, Math.max(20, sp.f)) / sr;
        const cos = Math.cos(w0); const sin = Math.sin(w0);
        let b0, b1, b2, a0, a1, a2;
        if (sp.kind === "peak") {
          const alpha = sin / (2 * sp.q);
          b0 = 1 + alpha * A; b1 = -2 * cos; b2 = 1 - alpha * A;
          a0 = 1 + alpha / A; a1 = -2 * cos; a2 = 1 - alpha / A;
        } else {
          const alpha = (sin / 2) * Math.SQRT2;
          const t = 2 * Math.sqrt(A) * alpha;
          if (sp.kind === "low") {
            b0 = A * ((A + 1) - (A - 1) * cos + t);
            b1 = 2 * A * ((A - 1) - (A + 1) * cos);
            b2 = A * ((A + 1) - (A - 1) * cos - t);
            a0 = (A + 1) + (A - 1) * cos + t;
            a1 = -2 * ((A - 1) + (A + 1) * cos);
            a2 = (A + 1) + (A - 1) * cos - t;
          } else {
            b0 = A * ((A + 1) + (A - 1) * cos + t);
            b1 = -2 * A * ((A - 1) + (A + 1) * cos);
            b2 = A * ((A + 1) + (A - 1) * cos - t);
            a0 = (A + 1) - (A - 1) * cos + t;
            a1 = 2 * ((A - 1) - (A + 1) * cos);
            a2 = (A + 1) - (A - 1) * cos - t;
          }
        }
        b.b0 = b0 / a0; b.b1 = b1 / a0; b.b2 = b2 / a0; b.a1 = a1 / a0; b.a2 = a2 / a0;
      }
    }
    let s = fxFinite(input);
    for (const b of rt.bands) {
      const y = b.b0 * s + b.b1 * b.x1 + b.b2 * b.x2 - b.a1 * b.y1 - b.a2 * b.y2;
      b.x2 = b.x1; b.x1 = s; b.y2 = b.y1; b.y1 = fxFinite(y); s = b.y1;
    }
    return s;
  }
  return fxFinite(input);
}
function processFxChain(input, nodes, runtime, sr) {
  let sample = fxFinite(input);
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]; const rt = runtime[i];
    if (!node || !rt || !node.enabled) continue;
    const wet = Math.min(1, Math.max(0, node.wet));
    if (wet <= 0) continue;
    const processed = fxFinite(processFxOne(sample, node, rt, sr));
    sample = fxFinite(sample * (1 - wet) + processed * wet);
  }
  return sample;
}

function createEnvelope() {
  return { stage: "idle", level: 0, stageTime: 0, releaseFrom: 0 };
}
function createNoteFxState() {
  return {
    vibratoSpeedHz: 0, vibratoDepthSemi: 0, vibratoPhase: 0,
    slideSemiPerSec: 0, portaSemiPerSec: 0, cutoffOct: 0,
    arpSemi1: 0, arpSemi2: 0, arpHz: 0, arpPhase: 0,
  };
}
function resetNoteFxFromParams(state, params) {
  state.vibratoSpeedHz = (params && params.vibratoSpeedHz) || 0;
  state.vibratoDepthSemi = (params && params.vibratoDepthSemi) || 0;
  state.vibratoPhase = 0;
  state.slideSemiPerSec = (params && params.slideSemiPerSec) || 0;
  state.portaSemiPerSec = (params && params.portaSemiPerSec) || 0;
  state.cutoffOct = (params && params.cutoffOct) || 0;
  state.arpSemi1 = (params && params.arpSemi1) || 0;
  state.arpSemi2 = (params && params.arpSemi2) || 0;
  state.arpHz = (params && params.arpHz) || 0;
  state.arpPhase = 0;
}
function patchNoteFxFromParams(state, params) {
  if (!params) return;
  if (params.vibratoSpeedHz !== undefined) state.vibratoSpeedHz = params.vibratoSpeedHz;
  if (params.vibratoDepthSemi !== undefined) state.vibratoDepthSemi = params.vibratoDepthSemi;
  if (params.slideSemiPerSec !== undefined) state.slideSemiPerSec = params.slideSemiPerSec;
  if (params.portaSemiPerSec !== undefined) state.portaSemiPerSec = params.portaSemiPerSec;
  if (params.cutoffOct !== undefined) state.cutoffOct = params.cutoffOct;
  if (params.arpSemi1 !== undefined || params.arpSemi2 !== undefined || params.arpHz !== undefined) {
    if (params.arpSemi1 !== undefined) state.arpSemi1 = params.arpSemi1;
    if (params.arpSemi2 !== undefined) state.arpSemi2 = params.arpSemi2;
    if (params.arpHz !== undefined) state.arpHz = params.arpHz;
    state.arpPhase = 0;
  }
}
function advanceNoteFxPitch(state, voice, dt, presetGlideMs) {
  if (state.slideSemiPerSec !== 0) {
    const ratio = Math.pow(2, (state.slideSemiPerSec * dt) / 12);
    voice.targetFreqHz *= ratio;
    if (state.portaSemiPerSec <= 0) voice.freqHz *= ratio;
  }
  if (state.portaSemiPerSec > 0) {
    const cur = Math.log2(Math.max(voice.freqHz, 1e-3));
    const tgt = Math.log2(Math.max(voice.targetFreqHz, 1e-3));
    const maxStep = (state.portaSemiPerSec / 12) * dt;
    const delta = Math.min(maxStep, Math.max(-maxStep, tgt - cur));
    voice.freqHz = Math.pow(2, cur + delta);
  } else if (presetGlideMs && presetGlideMs > 0) {
    const glide = Math.exp(-dt * (1000 / presetGlideMs));
    voice.freqHz = voice.targetFreqHz + (voice.freqHz - voice.targetFreqHz) * glide;
  } else {
    voice.freqHz = voice.targetFreqHz;
  }
  let pitchSemi = 0;
  if (state.arpHz > 0) {
    const tones = [0];
    if (state.arpSemi1 > 0) tones.push(state.arpSemi1);
    if (state.arpSemi2 > 0) tones.push(state.arpSemi2);
    if (tones.length > 1) {
      state.arpPhase += state.arpHz * dt;
      pitchSemi = tones[Math.floor(state.arpPhase) % tones.length];
    }
  }
  if (state.vibratoSpeedHz > 0 && state.vibratoDepthSemi > 0) {
    state.vibratoPhase += state.vibratoSpeedHz * dt;
    pitchSemi += state.vibratoDepthSemi * Math.sin(2 * Math.PI * (state.vibratoPhase % 1));
  }
  return pitchSemi;
}
function createVoice() {
  return {
    active: false, note: 60, velocity: 0, freqHz: 440, targetFreqHz: 440,
    osc1Phase: 0, osc2Phase: 0, osc3Phase: 0, ampEnv: createEnvelope(), modEnv: createEnvelope(),
    lfoPhase: 0, filterZ1: 0, filterZ2: 0, noiseState: 1, noteFx: createNoteFxState(),
  };
}
function triggerEnvelope(env) { env.stage = "attack"; env.stageTime = 0; env.level = 0; }
function softTriggerEnvelope(env, adsr) {
  const a = Math.max(adsr.a, 1e-4);
  const level = clamp(env.level, 0, 1);
  env.stage = "attack";
  env.stageTime = level * a;
  env.level = level;
}
function releaseEnvelope(env) {
  if (env.stage === "idle") return;
  env.releaseFrom = env.level; env.stage = "release"; env.stageTime = 0;
}
function advanceEnvelope(env, adsr, dt) {
  if (env.stage === "idle") { env.level = 0; return 0; }
  env.stageTime += dt;
  if (env.stage === "attack") {
    const a = Math.max(adsr.a, 1e-4);
    env.level = clamp(env.stageTime / a, 0, 1);
    if (env.stageTime >= a) { env.stage = "decay"; env.stageTime = 0; env.level = 1; }
  } else if (env.stage === "decay") {
    const d = Math.max(adsr.d, 1e-4);
    const t = clamp(env.stageTime / d, 0, 1);
    env.level = 1 + (adsr.s - 1) * t;
    if (env.stageTime >= d) { env.stage = "sustain"; env.stageTime = 0; env.level = adsr.s; }
  } else if (env.stage === "sustain") {
    env.level = adsr.s;
  } else if (env.stage === "release") {
    const r = Math.max(adsr.r, 1e-4);
    const t = clamp(env.stageTime / r, 0, 1);
    env.level = env.releaseFrom * (1 - t);
    if (env.stageTime >= r || env.level <= 1e-4) { env.stage = "idle"; env.level = 0; }
  }
  return env.level;
}
function oscSample(wave, phase, pwm, noiseState) {
  const p = phase - Math.floor(phase);
  if (wave === "sine") return Math.sin(2 * Math.PI * p);
  if (wave === "tri") return 1 - 4 * Math.abs(p - 0.5);
  if (wave === "saw") return 2 * p - 1;
  if (wave === "square") return p < clamp(pwm, 0.05, 0.95) ? 1 : -1;
  let x = noiseState.value | 0;
  x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
  noiseState.value = x;
  return (x >>> 0) / 0xffffffff * 2 - 1;
}
function applyFilter(input, voice, type, cutoffHz, resonance, sampleRate) {
  const fc = clamp(cutoffHz, 20, sampleRate * 0.2);
  const f = Math.min(0.95, 2 * Math.sin(Math.PI * (fc / sampleRate)));
  const q = clamp(1 - resonance * 0.9, 0.1, 1);
  const low = voice.filterZ1 + f * voice.filterZ2;
  const high = input - low - q * voice.filterZ2;
  const band = voice.filterZ2 + f * high;
  if (!Number.isFinite(low) || !Number.isFinite(band) || !Number.isFinite(high)) {
    voice.filterZ1 = 0; voice.filterZ2 = 0; return 0;
  }
  voice.filterZ2 = clamp(band, -8, 8);
  voice.filterZ1 = clamp(low, -8, 8);
  if (type === "highpass") return clamp(high, -8, 8);
  if (type === "bandpass") return voice.filterZ2;
  return voice.filterZ1;
}
function modAmount(routes, source, target, value, polarity) {
  let sum = 0;
  for (const route of routes) {
    if (route.source !== source || route.target !== target) continue;
    const v = route.invert
      ? (polarity === "bipolar" ? -value : 1 - value)
      : value;
    sum += v * route.depth;
  }
  return sum;
}
function keyTrackOctaves(note) { return (note - 60) / 12; }

class TsAudioKernel {
  constructor() {
    this.sampleRate = 0;
    this.playing = false;
    this.bpm = 120;
    this.transportStartTime = 0;
    this.presets = DEFAULT_PRESETS.map(clonePreset);
    this.fxNodes = [];
    this.fxRuntime = [];
    this.voices = Array.from({ length: INSTRUMENT_SLOT_COUNT }, () => createVoice());
    this.pending = [];
    this.noiseScratch = { value: 1 };
  }
  init(options) { this.sampleRate = options.sampleRate; }
  handleMessage(message) {
    if (message.type === "init") { this.init({ sampleRate: message.sampleRate }); return; }
    if ((message.type === "noteOn" || message.type === "noteOff" || message.type === "setNoteFx") && message.time > 0) {
      this.pending.push({ time: message.time, message });
      this.pending.sort((a, b) => a.time - b.time);
      return;
    }
    this.applyMessage(message);
  }
  process(left, right, frames, currentTime) {
    if (this.sampleRate <= 0) {
      left.fill(0, 0, frames);
      if (right !== left) right.fill(0, 0, frames);
      return;
    }
    const dt = 1 / this.sampleRate;
    let pendingIndex = 0;
    for (let i = 0; i < frames; i++) {
      const t = currentTime + i * dt;
      while (pendingIndex < this.pending.length) {
        const event = this.pending[pendingIndex];
        if (!event || event.time > t) break;
        this.applyMessage(event.message);
        pendingIndex++;
      }
      let sample = 0;
      for (let slot = 0; slot < INSTRUMENT_SLOT_COUNT; slot++) sample += this.renderVoice(slot, dt);
      sample = processFxChain(sample * 0.35, this.fxNodes, this.fxRuntime, this.sampleRate);
      const out = Number.isFinite(sample) ? clamp(sample, -1, 1) : 0;
      left[i] = out;
      right[i] = out;
    }
    }
    if (pendingIndex > 0) this.pending = this.pending.slice(pendingIndex);
  }
  applyMessage(message) {
    const sr = this.sampleRate || 48000;
    switch (message.type) {
      case "setBpm": this.bpm = message.bpm; break;
      case "transport":
        this.playing = message.playing;
        this.transportStartTime = message.startTime;
        break;
      case "setInstrument": {
        const instrument = message.instrument;
        const preset = instrument && instrument.kind === "synth"
          ? instrument.preset
          : (message.preset || this.presets[message.slot]);
        if (preset) this.presets[message.slot] = clonePreset(preset);
        break;
      }
      case "setFxStack":
        this.fxNodes = message.effects.map(clonePreset);
        this.fxRuntime = this.fxNodes.map(n => createFxRuntime(n, sr));
        break;
      case "setFx": {
        const existing = this.fxNodes[message.index];
        if (existing) {
          const next = Object.assign({}, existing, message.patch, {
            params: Object.assign({}, existing.params, (message.patch && message.patch.params) || {}),
          });
          this.fxNodes[message.index] = next;
          if (message.patch && message.patch.type && message.patch.type !== existing.type) {
            this.fxRuntime[message.index] = createFxRuntime(next, sr);
          } else if (!this.fxRuntime[message.index]) {
            this.fxRuntime[message.index] = createFxRuntime(next, sr);
          }
        }
        break;
      }
      case "clearFx": this.fxNodes = []; this.fxRuntime = []; break;
      case "noteOn": this.noteOn(message.slot, message.note, message.velocity, message.noteFx); break;
      case "noteOff": this.noteOff(message.slot); break;
      case "setNoteFx": this.setNoteFx(message.slot, message.noteFx, message.velocity); break;
      case "allNotesOff": this.allNotesOff(); break;
    }
  }
  noteOn(slot, note, velocity, noteFx) {
    const voice = this.voices[slot];
    const preset = this.presets[slot];
    if (!voice || !preset) return;
    const wasActive = voice.ampEnv.stage !== "idle";
    const porta = (noteFx && noteFx.portaSemiPerSec) || 0;
    const hardRetrigger = !!(noteFx && noteFx.hardRetrigger);
    voice.active = true; voice.note = note; voice.velocity = clamp(velocity, 0, 1);
    const base = midiToHz(note);
    voice.targetFreqHz = base;
    resetNoteFxFromParams(voice.noteFx, noteFx);
    if (wasActive && porta > 0 && !hardRetrigger) {
      voice.noteFx.portaSemiPerSec = porta;
      return;
    }
    if (!wasActive || hardRetrigger || !preset.glideMs || preset.glideMs <= 0) voice.freqHz = base;
    if (!wasActive || hardRetrigger) {
      voice.osc1Phase = 0; voice.osc2Phase = 0; voice.osc3Phase = 0;
      triggerEnvelope(voice.ampEnv); triggerEnvelope(voice.modEnv);
    } else {
      softTriggerEnvelope(voice.ampEnv, preset.ampEnv);
      softTriggerEnvelope(voice.modEnv, preset.modEnv);
    }
  }
  setNoteFx(slot, noteFx, velocity) {
    const voice = this.voices[slot];
    if (!voice || !voice.active) return;
    patchNoteFxFromParams(voice.noteFx, noteFx);
    if (velocity !== undefined) voice.velocity = clamp(velocity, 0, 1);
  }
  noteOff(slot) {
    const voice = this.voices[slot];
    if (!voice || !voice.active) return;
    releaseEnvelope(voice.ampEnv); releaseEnvelope(voice.modEnv);
  }
  allNotesOff() { this.pending = []; for (let s = 0; s < INSTRUMENT_SLOT_COUNT; s++) this.noteOff(s); }
  renderVoice(slot, dt) {
    const voice = this.voices[slot];
    const preset = this.presets[slot];
    if (!voice || !preset || voice.ampEnv.stage === "idle") { if (voice) voice.active = false; return 0; }
    const amp = advanceEnvelope(voice.ampEnv, preset.ampEnv, dt);
    const mod = advanceEnvelope(voice.modEnv, preset.modEnv, dt);
    const vibratoSemi = advanceNoteFxPitch(voice.noteFx, voice, dt, preset.glideMs);
    voice.lfoPhase += preset.lfo.rateHz * dt;
    const lfoWave = preset.lfo.wave === "tri"
      ? 1 - 4 * Math.abs((voice.lfoPhase % 1) - 0.5)
      : Math.sin(2 * Math.PI * (voice.lfoPhase % 1));
    const lfo = lfoWave * preset.lfo.depth;
    const key = keyTrackOctaves(voice.note);
    const pitchMod = modAmount(preset.routes, "modEnv", "pitch", mod)
      + modAmount(preset.routes, "ampEnv", "pitch", amp)
      + modAmount(preset.routes, "lfo", "pitch", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "pitch", key, "bipolar")
      + vibratoSemi;
    const cutoffMod = modAmount(preset.routes, "modEnv", "cutoff", mod)
      + modAmount(preset.routes, "ampEnv", "cutoff", amp)
      + modAmount(preset.routes, "lfo", "cutoff", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "cutoff", key, "bipolar")
      + voice.noteFx.cutoffOct;
    const ampMod = modAmount(preset.routes, "modEnv", "amp", mod)
      + modAmount(preset.routes, "lfo", "amp", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "amp", key, "bipolar");
    const pwmMod = modAmount(preset.routes, "modEnv", "pwm", mod)
      + modAmount(preset.routes, "lfo", "pwm", (lfo + 1) * 0.5)
      + modAmount(preset.routes, "key", "pwm", key, "bipolar");
    const freq = voice.freqHz * Math.pow(2, pitchMod / 12);
    const o1 = preset.osc1, o2 = preset.osc2;
    const f1 = freq * Math.pow(2, o1.octave + o1.semis / 12) * Math.pow(2, o1.detuneCents / 1200);
    const f2 = freq * Math.pow(2, o2.octave + o2.semis / 12) * Math.pow(2, o2.detuneCents / 1200);
    voice.osc1Phase += f1 / this.sampleRate;
    voice.osc2Phase += f2 / this.sampleRate;
    const pwm1 = clamp((o1.pwm == null ? 0.5 : o1.pwm) + pwmMod * 0.1, 0.05, 0.95);
    const pwm2 = clamp((o2.pwm == null ? 0.5 : o2.pwm) + pwmMod * 0.1, 0.05, 0.95);
    this.noiseScratch.value = voice.noiseState;
    const s1 = oscSample(o1.wave, voice.osc1Phase, pwm1, this.noiseScratch) * o1.level;
    const s2 = oscSample(o2.wave, voice.osc2Phase, pwm2, this.noiseScratch) * o2.level;
    voice.noiseState = this.noiseScratch.value;
    let mixed = s1 + s2;
    mixed = applyFilter(mixed, voice, preset.filter.type, preset.filter.cutoffHz * Math.pow(2, cutoffMod), preset.filter.resonance, this.sampleRate);
    const out = mixed * amp * voice.velocity * (1 + ampMod);
    if (!Number.isFinite(out)) { voice.filterZ1 = 0; voice.filterZ2 = 0; return 0; }
    return out;
  }
}

class CurvaAudioProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super(options);
    this.kernel = new TsAudioKernel();
    this.kernel.init({ sampleRate });
    this.port.onmessage = (event) => this.kernel.handleMessage(event.data);
    this.port.postMessage({ type: "ready", sampleRate });
  }
  process(_inputs, outputs) {
    const channels = outputs[0];
    const left = channels && channels[0];
    const right = (channels && channels[1]) || left;
    if (!left || !right) return true;
    this.kernel.process(left, right, left.length, currentTime);
    return true;
  }
}
registerProcessor("${CURVA_AUDIO_PROCESSOR_NAME}", CurvaAudioProcessor);
`;

  const blob = new Blob([source], { type: "application/javascript" });
  return URL.createObjectURL(blob);
}

/** Compact defaults for the blob worklet (mirrors defaultPresets). */
function minimalDefaultPresets() {
  return [
    {
      name: "Bass",
      osc1: { wave: "saw", octave: -1, semis: 0, detuneCents: 0, level: 0.7 },
      osc2: { wave: "square", octave: -1, semis: 0, detuneCents: -8, level: 0.35, pwm: 0.45 },
      filter: { type: "lowpass", cutoffHz: 420, resonance: 0.25 },
      ampEnv: { a: 0.005, d: 0.15, s: 0.7, r: 0.2 },
      modEnv: { a: 0.001, d: 0.2, s: 0, r: 0.05 },
      lfo: { rateHz: 0, wave: "sine", depth: 0 },
      routes: [{ source: "modEnv", target: "cutoff", depth: 1.5 }],
    },
    {
      name: "Lead",
      osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: 0, level: 0.55 },
      osc2: { wave: "square", octave: 0, semis: 0, detuneCents: 7, level: 0.4, pwm: 0.5 },
      filter: { type: "lowpass", cutoffHz: 2400, resonance: 0.15 },
      ampEnv: { a: 0.01, d: 0.2, s: 0.55, r: 0.25 },
      modEnv: { a: 0.01, d: 0.1, s: 0.5, r: 0.1 },
      lfo: { rateHz: 5, wave: "sine", depth: 0.15 },
      routes: [{ source: "lfo", target: "pitch", depth: 0.15 }],
      glideMs: 20,
    },
    {
      name: "Kick",
      osc1: { wave: "sine", octave: -1, semis: 0, detuneCents: 0, level: 1 },
      osc2: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.12 },
      filter: { type: "lowpass", cutoffHz: 800, resonance: 0.2 },
      ampEnv: { a: 0.001, d: 0.22, s: 0, r: 0.06 },
      modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.01 },
      lfo: { rateHz: 0, wave: "sine", depth: 0 },
      routes: [
        { source: "modEnv", target: "pitch", depth: 24 },
        { source: "modEnv", target: "cutoff", depth: 2 },
      ],
    },
    {
      name: "Hat",
      osc1: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.85 },
      osc2: { wave: "square", octave: 2, semis: 0, detuneCents: 0, level: 0.15, pwm: 0.1 },
      filter: { type: "highpass", cutoffHz: 6000, resonance: 0.1 },
      ampEnv: { a: 0.001, d: 0.06, s: 0, r: 0.04 },
      modEnv: { a: 0.001, d: 0.05, s: 0, r: 0.02 },
      lfo: { rateHz: 0, wave: "sine", depth: 0 },
      routes: [{ source: "modEnv", target: "cutoff", depth: 0.5 }],
    },
    {
      name: "Pad",
      osc1: { wave: "tri", octave: -1, semis: 0, detuneCents: 0, level: 0.55 },
      osc2: { wave: "saw", octave: -1, semis: 0, detuneCents: 5, level: 0.3 },
      filter: { type: "lowpass", cutoffHz: 900, resonance: 0.2 },
      ampEnv: { a: 0.02, d: 0.25, s: 0.6, r: 0.35 },
      modEnv: { a: 0.01, d: 0.3, s: 0.2, r: 0.2 },
      lfo: { rateHz: 0.4, wave: "sine", depth: 0.2 },
      routes: [{ source: "lfo", target: "cutoff", depth: 0.8 }],
    },
    {
      name: "Arp",
      osc1: { wave: "square", octave: 0, semis: 0, detuneCents: 0, level: 0.5, pwm: 0.35 },
      osc2: { wave: "square", octave: 1, semis: 0, detuneCents: -6, level: 0.35, pwm: 0.5 },
      filter: { type: "bandpass", cutoffHz: 1800, resonance: 0.35 },
      ampEnv: { a: 0.005, d: 0.12, s: 0.4, r: 0.15 },
      modEnv: { a: 0.01, d: 0.1, s: 0.5, r: 0.1 },
      lfo: { rateHz: 6, wave: "tri", depth: 0.25 },
      routes: [{ source: "lfo", target: "pwm", depth: 0.4 }],
      glideMs: 12,
    },
    {
      name: "Snare",
      osc1: { wave: "noise", octave: 0, semis: 0, detuneCents: 0, level: 0.55 },
      osc2: { wave: "tri", octave: 1, semis: 0, detuneCents: 0, level: 0.25 },
      filter: { type: "highpass", cutoffHz: 2500, resonance: 0.15 },
      ampEnv: { a: 0.001, d: 0.1, s: 0, r: 0.05 },
      modEnv: { a: 0.001, d: 0.08, s: 0, r: 0.02 },
      lfo: { rateHz: 0, wave: "sine", depth: 0 },
      routes: [{ source: "modEnv", target: "cutoff", depth: 1.2 }],
    },
    {
      name: "Strings",
      osc1: { wave: "saw", octave: 0, semis: 0, detuneCents: 0, level: 0.4 },
      osc2: { wave: "saw", octave: 0, semis: 0, detuneCents: 12, level: 0.4 },
      filter: { type: "lowpass", cutoffHz: 3200, resonance: 0.1 },
      ampEnv: { a: 0.05, d: 0.4, s: 0.7, r: 0.6 },
      modEnv: { a: 0.1, d: 0.5, s: 0.4, r: 0.4 },
      lfo: { rateHz: 0.2, wave: "sine", depth: 0.3 },
      routes: [
        { source: "lfo", target: "pitch", depth: 0.1 },
        { source: "modEnv", target: "cutoff", depth: 1.5 },
      ],
      glideMs: 40,
    },
  ];
}
